import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { GameEvent } from '../game/types'
import { useLang } from '../i18n/LangContext'
import { SportIcon, sportOfId } from './SportIcon'

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`
}

export function EventCard({
  ev,
  index,
  status,
  revealed,
  shake,
}: {
  ev: GameEvent
  index: number
  status: 'idle' | 'correct'
  revealed: boolean
  shake: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: ev.id, disabled: revealed })
  const { t } = useLang()

  const sport = sportOfId(ev.id)
  const sportName = t.categories[sport]

  const className = [
    'card',
    status === 'correct' ? 'correct' : '',
    revealed ? 'revealed' : '',
    isDragging ? 'dragging' : '',
    shake ? 'shake' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <li
      ref={setNodeRef}
      className={className}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...(revealed ? {} : listeners)}
      aria-label={`Position ${index + 1}: ${ev.title}`}
    >
      <span className="idx" aria-hidden="true">
        {index + 1}
      </span>
      <span className="spine" aria-hidden="true">
        <span className="node" />
      </span>
      <span className={`mark mark-${sport}`} aria-hidden="true">
        <SportIcon sport={sport} size={20} />
      </span>
      <span className="card-body">
        <span className="card-title">{ev.title}</span>
        <span className="card-meta">
          {revealed && ev.date && <em className="date">{formatDate(ev.date)}</em>}
          <span className="sport">{sportName}</span>
        </span>
        {revealed && ev.blurb && <span className="blurb">{ev.blurb}</span>}
      </span>
      {!revealed && (
        <span className="grip" aria-hidden="true">
          <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor">
            <circle cx="4" cy="5" r="1.4" />
            <circle cx="10" cy="5" r="1.4" />
            <circle cx="4" cy="10" r="1.4" />
            <circle cx="10" cy="10" r="1.4" />
            <circle cx="4" cy="15" r="1.4" />
            <circle cx="10" cy="15" r="1.4" />
          </svg>
        </span>
      )}
    </li>
  )
}
